import React, { useState } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
import Select from './Select';
import useEscapeKey from '../utils/useEscapeKey';

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'yearly', label: 'Yearly' }
];

const RecurringExpenseModal = ({ onClose, onSave, expense }) => {
  const isEdit = !!expense;
  useEscapeKey(onClose);
  const [formData, setFormData] = useState({
    vendorName: expense?.vendorName || '',
    amount: expense?.amount || '',
    notes: expense?.notes || '',
    frequency: expense?.frequency || 'monthly',
    startDate: expense?.startDate || ''
  });
  const [gstIncluded, setGstIncluded] = useState(expense?.gstIncluded || false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const success = await onSave({ ...formData, gstIncluded }, expense?.id);
    setIsLoading(false);
    if (success) onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex-between mb-lg">
          <h3>{isEdit ? 'Edit Recurring Expense' : 'Add Recurring Expense'}</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Vendor Name *</label>
            <input
              type="text"
              name="vendorName"
              className="form-input"
              value={formData.vendorName}
              onChange={handleChange}
              placeholder="e.g., Netflix, Rent, etc."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Amount *</label>
            <input
              type="number"
              name="amount"
              className="form-input"
              value={formData.amount}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              required
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={gstIncluded}
                onChange={(e) => setGstIncluded(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)' }}
              />
              <span className="form-label" style={{ margin: 0 }}>Amount includes 18% GST</span>
            </label>
            {gstIncluded && formData.amount && (
              <div style={{
                marginTop: 'var(--spacing-xs)',
                padding: 'var(--spacing-sm)',
                backgroundColor: 'var(--color-gray-50)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-secondary)'
              }}>
                Base: {formatCurrency(parseFloat(formData.amount) / 1.18)} + GST: {formatCurrency(parseFloat(formData.amount) - parseFloat(formData.amount) / 1.18)} = Total: {formatCurrency(formData.amount)}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Frequency *</label>
            <Select
              value={formData.frequency}
              onChange={v => setFormData({ ...formData, frequency: v })}
              options={FREQUENCY_OPTIONS}
            />
          </div>

          {!isEdit && (
            <div className="form-group">
              <label className="form-label">From Date *</label>
              <input
                type="date"
                name="startDate"
                className="form-input"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              name="notes"
              className="form-input"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Additional details..."
            />
          </div>

          <div className="flex gap-md">
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? (
                <span className="flex-center gap-sm">
                  <div className="spinner spinner-sm"></div>
                  Saving...
                </span>
              ) : (
                isEdit ? 'Update' : 'Create Recurring Expense'
              )}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecurringExpenseModal;
